import { BRAND_LIMITS, brandListItemKey, normalizeBrandListItem } from '@mg/shared';

/**
 * Adding to and removing from the Vocabulary tag lists and the Writing guidelines table (spec:261-262,
 * :265, :269; web.md §3.4). Pure: TagListInput and GuidelinesTable call these, and the wizard and the
 * Edit page save the lists they produce.
 *
 * Only what is ADDED is checked. Items already stored that break today's rules (too long, duplicates,
 * more than 50: Social hashtags and Catchphrases saved on the old page, E4) stay exactly as they are,
 * and each can still be removed on its own.
 */

/** What adding one entry did. A list only changes on `{ items }`. */
export type AddTagOutcome =
  | { items: string[] }
  /** empty: nothing typed (no message; the draft stays). tooLong / full: say so, add nothing. */
  | { error: 'empty' | 'tooLong' | 'full' }
  /** The entry is already in the list (case-insensitive): add nothing, clear the draft, say so. */
  | { duplicate: string };

export interface AddTagOptions {
  /** How a typed entry is cleaned up before it is checked; default normalizeTagText. */
  normalize?: (raw: string) => string;
  maxItems: number;
  maxLength: number;
}

/**
 * One line, the way the server stores a list item: control characters removed, whitespace runs made
 * one space, trimmed (`normalizeBrandListItem`, which the brand writer applies too). Any list but
 * Social hashtags.
 */
export function normalizeTagText(raw: string): string {
  return normalizeBrandListItem('keywords', raw);
}

/** A Social hashtag: one line, leading `#`s dropped, then exactly one `#` (today's rule); '' when empty. */
export function normalizeHashtag(raw: string): string {
  return normalizeBrandListItem('socialHashtags', raw);
}

/**
 * Adds one typed entry to a list, in order:
 * 1. normalized; nothing left adds nothing (`empty`);
 * 2. a list already at `maxItems` takes no more (`full`; the box is disabled there anyway);
 * 3. longer than `maxLength` after normalizing (`tooLong`);
 * 4. the same entry as one already in the list, ignoring case and whitespace runs, as the server
 *    compares them (`duplicate`, naming the stored entry);
 * 5. otherwise the entry goes on the end.
 */
export function addTag(items: readonly string[], raw: string, options: AddTagOptions): AddTagOutcome {
  const value = (options.normalize ?? normalizeTagText)(raw);
  if (value === '') return { error: 'empty' };
  if (items.length >= options.maxItems) return { error: 'full' };
  if (value.length > options.maxLength) return { error: 'tooLong' };
  const key = brandListItemKey(value);
  const existing = items.find((item) => brandListItemKey(item) === key);
  if (existing !== undefined) return { duplicate: existing };
  return { items: [...items, value] };
}

/** A Vocabulary tag: 60 characters, 50 per list. */
export function addVocabularyTag(
  items: readonly string[],
  raw: string,
  normalize: (raw: string) => string = normalizeTagText,
): AddTagOutcome {
  return addTag(items, raw, { normalize, maxItems: BRAND_LIMITS.tagsPerList, maxLength: BRAND_LIMITS.tag });
}

/** A writing guideline: 300 characters, 50 rules. */
export function addGuideline(items: readonly string[], raw: string): AddTagOutcome {
  return addTag(items, raw, { maxItems: BRAND_LIMITS.guidelines, maxLength: BRAND_LIMITS.guideline });
}

/**
 * The list without the entry at `index`. By position, never by value: a stored list may hold the same
 * text twice, and removing one must leave the other.
 */
export function removeAt(items: readonly string[], index: number): string[] {
  return items.filter((_, i) => i !== index);
}

/** The messages for a refused tag (web.md §3.4). */
export const TAG_RULE_TEXT = {
  tooLong: `Keep each entry to ${BRAND_LIMITS.tag} characters or fewer.`,
  full: `Maximum of ${BRAND_LIMITS.tagsPerList}. Remove one to add another.`,
} as const;

/** The messages for a refused writing guideline (web.md §3.4). */
export const GUIDELINE_RULE_TEXT = {
  tooLong: `Keep each guideline to ${BRAND_LIMITS.guideline} characters or fewer.`,
  full: `Maximum of ${BRAND_LIMITS.guidelines} guidelines. Remove one to add another.`,
} as const;

/** What the list's polite live region says after an add, a remove or a duplicate. */
export const listAnnouncement = {
  added: (item: string) => `Added ${item}`,
  removed: (item: string) => `Removed ${item}`,
  duplicate: (item: string) => `${item} is already in the list.`,
};
